import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { usePageTitle } from "src/app/title-management/usePageTitle";

import ConfigurationSection from "./components/ConfigurationSection";
import ReportPreview from "./components/ReportPreview";
import ReportViewSection from "./components/ReportViewSection";
import {
  loadReportingDataset,
  loadReportingDatasetStatus,
  refreshReportingDataset,
  ReportingUnavailableError,
  saveReportingConfiguration,
} from "./data/reportingDataSource";
import { getAvailablePeriods, getFilterOptions } from "./model/calculations";
import { getBreakdownOptions } from "./model/defaults";
import { loadReportView, saveReportView } from "./model/reportViewStorage";
import {
  MappingKey,
  ReportConfiguration,
  ReportState,
  ReportView,
  ReportingDataset,
} from "./model/types";

import "./FinanceReporting.scss";

/** How long to wait between polls while the first snapshot is being built. */
const BUILD_POLL_INTERVAL_MS = 30_000;
const REFRESH_POLL_ATTEMPTS = 75;

/**
 * Finance Reporting - the workspace financial report builder.
 *
 * Client-facing name: "Reporting". Route: /workspace/:workspaceUuid/reporting
 *
 * This module is deliberately self-contained: it owns its view state locally, its styles
 * are all scoped under `.finance-reporting`, and its only imports from the host app are
 * the router, `usePageTitle` and `API_URLS`. See README.md before changing that.
 */
interface FinanceReportingProps {
  canConfigure: boolean;
}

export default function FinanceReporting(props: FinanceReportingProps) {
  const { canConfigure } = props;
  usePageTitle("Reporting");

  const { workspaceUuid } = useParams<{ workspaceUuid: string }>();

  const [dataset, setDataset] = useState<ReportingDataset | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /** Local, uncommitted edits to the admin configuration. */
  const [configuration, setConfiguration] =
    useState<ReportConfiguration | null>(null);
  const [view, setView] = useState<ReportView>(() =>
    loadReportView(workspaceUuid),
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const applyDataset = useCallback(
    (loaded: ReportingDataset, adoptConfiguration: boolean) => {
      setDataset(loaded);

      // Only take the server's configuration when the user has no edits in flight,
      // otherwise a background refresh would silently discard their work.
      if (adoptConfiguration) {
        setConfiguration(loaded.configuration);
      }
    },
    [],
  );

  const fetchDataset = useCallback(
    async (adoptConfiguration: boolean) => {
      try {
        const loaded = await loadReportingDataset(workspaceUuid);
        applyDataset(loaded, adoptConfiguration);
        setLoadError(null);
        return loaded;
      } catch (error) {
        if (error instanceof ReportingUnavailableError) {
          setLoadError("Reporting is not available for this workspace.");
          return null;
        }

        console.error(error);
        setLoadError("Could not load the reporting data.");
        return null;
      }
    },
    [workspaceUuid, applyDataset],
  );

  useEffect(() => {
    setDataset(null);
    setConfiguration(null);
    setView(loadReportView(workspaceUuid));
    setLoadError(null);
    fetchDataset(true);
  }, [workspaceUuid, fetchDataset]);

  // A saved view may refer to an additional dimension that an admin subsequently removes.
  // Fall back to a stable built-in breakdown instead of rendering an empty grouping.
  useEffect(() => {
    if (
      !configuration ||
      getBreakdownOptions(configuration).some(
        ({ key }) => key === view.breakdown,
      )
    ) {
      return;
    }

    setView((current: ReportView): ReportView => {
      const next: ReportView = { ...current, breakdown: "client" };
      saveReportView(workspaceUuid, next);
      return next;
    });
  }, [configuration, view.breakdown, workspaceUuid]);

  // Poll until the first snapshot lands or a stale snapshot has been rebuilt. A stale
  // snapshot remains visible, but is replaced as soon as the background work completes.
  useEffect(() => {
    if (dataset?.status !== "building" && !dataset?.isStale) return undefined;

    let cancelled = false;
    let timeout: number;

    const pollStatus = async () => {
      try {
        const status = await loadReportingDatasetStatus(workspaceUuid);
        if (cancelled) return;

        if (status.status !== "building") {
          // A building response has no server configuration; only now fetch the complete
          // dataset and adopt the effective configuration from its ready snapshot.
          fetchDataset(true);
          return;
        }
      } catch (error) {
        console.error(error);
      }

      if (!cancelled) {
        timeout = window.setTimeout(pollStatus, BUILD_POLL_INTERVAL_MS);
      }
    };

    timeout = window.setTimeout(pollStatus, BUILD_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [dataset, fetchDataset, workspaceUuid]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const updateConfiguration = useCallback(
    (patch: Partial<ReportConfiguration>) => {
      setConfiguration((current) =>
        current ? { ...current, ...patch } : current,
      );
    },
    [],
  );

  const updateView = useCallback(
    (patch: Partial<ReportView>) => {
      setView((current) => {
        const next = { ...current, ...patch };
        saveReportView(workspaceUuid, next);
        return next;
      });
    },
    [workspaceUuid],
  );

  const updateMapping = useCallback((key: MappingKey, uuid: string) => {
    setConfiguration((current) => {
      if (!current) return current;

      return {
        ...current,
        mappings: { ...current.mappings, [key]: uuid },
        // A different status field has different values, so any previous selection is
        // meaningless. Reset it rather than leaving stale values behind.
        includedStatuses: key === "status" ? [] : current.includedStatuses,
      };
    });
  }, []);

  const state: ReportState | null = useMemo(
    () => (configuration ? { configuration, view } : null),
    [configuration, view],
  );

  const isDirty = useMemo(() => {
    if (!dataset || !configuration) return false;
    return (
      JSON.stringify(configuration) !== JSON.stringify(dataset.configuration)
    );
  }, [dataset, configuration]);

  const availablePeriods = useMemo(
    () =>
      dataset && configuration
        ? getAvailablePeriods(dataset.records, state!)
        : [],
    [dataset, configuration, state],
  );

  const filterOptions = useMemo(
    () =>
      dataset && configuration
        ? getFilterOptions(dataset.records, state!)
        : null,
    [dataset, configuration, state],
  );

  const saveConfiguration = useCallback(async () => {
    if (!configuration) return;

    setIsSaving(true);

    try {
      await saveReportingConfiguration(workspaceUuid, configuration);
      // Re-read so the stored timestamp and any server-side normalisation are reflected.
      await fetchDataset(true);
      setToast("Configuration stored on the workspace");
    } catch (error) {
      console.error(error);
      setToast("Could not store the configuration");
    } finally {
      setIsSaving(false);
    }
  }, [configuration, workspaceUuid, fetchDataset]);

  /**
   * Front-end only: restores the last stored configuration, or the resolved defaults when
   * the workspace has never stored one. Nothing changes in the database until the user
   * stores it.
   */
  const resetConfiguration = useCallback(() => {
    if (!dataset) return;
    setConfiguration(dataset.configuration);
    setToast(
      dataset.isStored
        ? "Reverted to the stored configuration"
        : "Reverted to defaults - not stored",
    );
  }, [dataset]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    const previousGeneratedAt = dataset?.generatedAt ?? null;
    let dataRefreshed = false;

    try {
      await refreshReportingDataset(workspaceUuid);

      // The refresh endpoint only queues work. Keep the current report visible and replace
      // it once the worker has stored a new snapshot, rather than leaving the user on the
      // pre-refresh response indefinitely.
      for (let attempt = 0; attempt < REFRESH_POLL_ATTEMPTS; attempt += 1) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, BUILD_POLL_INTERVAL_MS);
        });

        const status = await loadReportingDatasetStatus(workspaceUuid);
        if (status.generatedAt !== previousGeneratedAt) {
          await fetchDataset(false);
          dataRefreshed = true;
          break;
        }
      }

      if (dataRefreshed) {
        setToast("Reporting data refreshed");
      } else {
        setToast("Rebuild is still running - try refreshing again shortly");
      }
    } catch (error) {
      console.error(error);
      setToast("Could not queue a rebuild");
    } finally {
      setIsRefreshing(false);
    }
  }, [dataset?.generatedAt, fetchDataset, workspaceUuid]);

  if (loadError) {
    return (
      <div className="finance-reporting finance-reporting--message">
        <p>{loadError}</p>
      </div>
    );
  }

  if (!dataset || !state || !configuration) {
    return (
      <div className="finance-reporting finance-reporting--message">
        <p>Loading reporting data...</p>
      </div>
    );
  }

  if (dataset.status === "building") {
    return (
      <div className="finance-reporting finance-reporting--message">
        <p>
          Building the reporting snapshot for this workspace. This runs once and
          then stays cached - the page will update automatically.
        </p>
      </div>
    );
  }

  if (dataset.status === "error" || dataset.status === "too_large") {
    return (
      <div className="finance-reporting finance-reporting--message">
        <p>
          The reporting snapshot could not be built
          {dataset.error ? `: ${dataset.error}` : "."}
        </p>
      </div>
    );
  }

  return (
    <div className="finance-reporting">
      <div className="finance-reporting__shell">
        <aside
          className="finance-reporting__panel"
          aria-label="Report configuration"
        >
          <div className="finance-reporting__panel-heading">
            <div>
              <p className="finance-reporting-eyebrow">Financial report</p>
              <h2>Report setup</h2>
            </div>
          </div>

          <ReportViewSection
            view={view}
            configuration={configuration}
            availablePeriods={availablePeriods}
            filterOptions={filterOptions}
            onChange={updateView}
          />
          {canConfigure && (
            <ConfigurationSection
              key={workspaceUuid}
              configuration={configuration}
              fields={dataset.fields}
              records={dataset.records}
              isDirty={isDirty}
              isStored={dataset.isStored}
              savedAt={dataset.savedAt}
              isSaving={isSaving}
              canSave={isDirty}
              onChange={updateConfiguration}
              onMappingChange={updateMapping}
              onSave={saveConfiguration}
              onReset={resetConfiguration}
            />
          )}
        </aside>

        <ReportPreview
          state={state}
          dataset={dataset}
          workspaceUuid={workspaceUuid}
          onRefresh={refreshData}
          isRefreshing={isRefreshing}
        />
      </div>

      {toast && <div className="finance-reporting__toast">{toast}</div>}
    </div>
  );
}
